import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import { formatAppDate } from '../../utils/dateUtils';
import GRNInspectionModal from '../../components/Quality/GRNInspectionModal';
import './WarehouseDashboard.css';

const FILTERS = ['QUARANTINE', 'FAILED', 'PASSED', 'ALL'];
const STATUS_CLASS = { PASSED: 'status-success', QUARANTINE: 'status-warning', FAILED: 'status-error' };

const formatMeasurements = (m) =>
  m && typeof m === 'object' ? Object.entries(m).map(([k, v]) => `${k}: ${v}`).join(' · ') : '';

/**
 * Quality Control → Receipts (#280): purchase order lines received in the warehouse (GRN items)
 * and their QC decision. Quarantined lines are pending: accept puts them in stock, reject keeps them out.
 */
const QCReceipts = ({ onChanged }) => {
  const { t } = useTranslation('qc');
  const [filter, setFilter] = useState('QUARANTINE');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const status = filter === 'ALL' ? '' : `&quality_status=${filter}`;
      const response = await apiFetch(getApiUrl(`/api/grn-items?limit=200${status}`));
      setItems(response.ok ? (await response.json()) || [] : []);
    } catch (error) {
      console.error('Error fetching receipts for QC:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return (
    <div className="tab-content with-padding">
      <div className="warehouse-section">
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {FILTERS.map(f => (
            <button
              key={f}
              className={`btn-action ${filter === f ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter(f)}
            >
              {t(`receipts.filters.${f}`, f)}
            </button>
          ))}
          <button className="btn-action btn-secondary" onClick={fetchItems} style={{ marginLeft: 'auto' }}>
            ↻ {t('receipts.refresh', 'Refresh')}
          </button>
        </div>

        {loading ? (
          <div className="loading-state">{t('overview.loading', 'Loading...')}</div>
        ) : (
          <div className="warehouse-table-container">
            <table className="warehouse-table">
              <thead>
                <tr>
                  <th>{t('receipts.fields.grn', 'GRN')}</th>
                  <th>{t('receipts.fields.po', 'PO')}</th>
                  <th>{t('receipts.fields.supplier', 'Supplier')}</th>
                  <th>{t('receipts.fields.material', 'Material')}</th>
                  <th>{t('receipts.fields.quantity', 'Quantity')}</th>
                  <th>{t('receipts.fields.receiptDate', 'Received')}</th>
                  <th>{t('receipts.fields.status', 'QC status')}</th>
                  <th>{t('receipts.fields.measurements', 'Measurements')}</th>
                  <th>{t('receipts.fields.checkedBy', 'Checked by')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      {t(`receipts.empty.${filter}`, t('receipts.empty.ALL', 'No receipts.'))}
                    </td>
                  </tr>
                ) : items.map(item => (
                  <tr key={item.grn_item_id}>
                    <td><strong>{item.grn_number}</strong></td>
                    <td>{item.po_number}</td>
                    <td>{item.supplier_name || '-'}</td>
                    <td><strong>{item.item_code}</strong> {item.item_name}</td>
                    <td>{parseFloat(item.quantity_received).toLocaleString()} {item.unit_of_measure}</td>
                    <td>{item.receipt_date ? formatAppDate(item.receipt_date) : '-'}</td>
                    <td>
                      <span className={`status-badge ${STATUS_CLASS[item.quality_status] || 'status-pending'}`}>
                        {t(`receipts.status.${item.quality_status}`, item.quality_status)}
                      </span>
                    </td>
                    <td>{formatMeasurements(item.measurements) || '-'}</td>
                    <td>{item.checked_by_name || '-'}</td>
                    <td>
                      <button className="btn-action btn-primary" onClick={() => setSelected(item)}>
                        {t('receipts.inspect', 'Inspect')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <GRNInspectionModal
          item={selected}
          onClose={() => setSelected(null)}
          onSaved={() => { fetchItems(); if (onChanged) onChanged(); }}
        />
      )}
    </div>
  );
};

export default QCReceipts;
